import {
  ArrowLeft,
  BriefcaseBusiness,
  ChevronDown,
  Copy,
  ExternalLink,
  EyeOff,
  Folder,
  GripVertical,
  MessageSquareText,
  Plus,
  RotateCcw,
  Search,
  Settings,
  Tag as TagIcon,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api.js";
import { useGroups } from "../components/AppShell.js";
import { AvailabilityPanel, availabilityLabels } from "../components/AvailabilityPanel.js";
import type { Assessment, JobDetail, JobListItem } from "../types.js";

const workplaceLabels: Record<string, string> = {
  remote: "远程",
  hybrid: "混合办公",
  onsite: "现场办公",
  unknown: "方式未知",
};

type ReviewStatus = "inbox" | "dismissed";

function editableReviewStatus(status: string): ReviewStatus {
  return status === "dismissed" ? "dismissed" : "inbox";
}

function formatRelative(value: string): string {
  const delta = Date.now() - new Date(value).getTime();
  const minutes = Math.max(1, Math.floor(delta / 60_000));
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  return `${days} 天前`;
}

function formatCompensation(job: JobListItem): { main: string; period: string } {
  const { minimum, maximum, currency, period } = job.compensation;
  if (minimum == null && maximum == null) return { main: "待确认", period: "未披露" };
  const symbol =
    { CNY: "¥", SGD: "S$", USD: "US$", JPY: "¥", EUR: "€", GBP: "£" }[currency ?? ""] ?? `${currency ?? ""} `;
  const compact = (value: number) => {
    const major = value / 100;
    if (major >= 1_000_000) return `${(major / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
    if (major >= 1_000) return `${Math.round(major / 1_000)}k`;
    return new Intl.NumberFormat("zh-CN").format(major);
  };
  const main =
    minimum != null && maximum != null
      ? `${symbol}${compact(minimum)}–${compact(maximum)}`
      : `${symbol}${compact((minimum ?? maximum)!)}+`;
  return {
    main,
    period: `/ ${{ hour: "小时", day: "天", week: "周", month: "月", year: "年" }[period ?? ""] ?? period ?? ""}`,
  };
}

export function JobsPage() {
  const groups = useGroups();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedId, setSelectedId] = useState(searchParams.get("job"));
  const [detailOpen, setDetailOpen] = useState(Boolean(searchParams.get("job")));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [mobileNewGroupOpen, setMobileNewGroupOpen] = useState(false);
  const [mobileNewGroupName, setMobileNewGroupName] = useState("");
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1101px)").matches);
  const [detailRatio, setDetailRatio] = useState(() => {
    const stored = Number(window.localStorage.getItem("roleworth:detail-ratio"));
    return Number.isFinite(stored) && stored >= 0.45 && stored <= 0.75 ? stored : 0.6;
  });
  const [resizing, setResizing] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const listGenerationRef = useRef(0);

  const listQuery = useMemo(() => {
    const source = new URLSearchParams(queryString);
    const params = new URLSearchParams();
    const query = source.get("q");
    if (query) params.set("q", query);
    const availability = source.get("availability");
    if (availability === "active") {
      params.append("availability", "open");
      params.append("availability", "unknown");
    } else if (availability) params.set("availability", availability);
    const group = source.get("group");
    if (group === "ungrouped") params.set("ungrouped", "true");
    else if (group) params.set("group_id", group);
    if (!isDesktop || !source.has("show_dismissed")) {
      params.set("exclude_status", "dismissed");
    }
    params.set("sort", "agent_score");
    params.set("order", "desc");
    params.set("limit", "100");
    return params.toString();
  }, [isDesktop, queryString]);
  const activeListQueryRef = useRef(listQuery);

  useEffect(() => {
    activeListQueryRef.current = listQuery;
  }, [listQuery]);

  const loadJobs = useCallback(async () => {
    const generation = ++listGenerationRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError("");
    try {
      const data = await api<{ items: JobListItem[]; next_cursor: string | null; total: number }>(
        `/api/v1/jobs?${listQuery}`,
      );
      if (generation !== listGenerationRef.current) return;
      setJobs(data.items);
      setTotal(data.total);
      setNextCursor(data.next_cursor);
      const requestedJob = new URLSearchParams(queryString).get("job");
      if (requestedJob && !data.items.some((job) => job.id === requestedJob)) {
        setDetailOpen(false);
        setSearchParams(
          (params) => {
            const next = new URLSearchParams(params);
            next.delete("job");
            return next;
          },
          { replace: true },
        );
      }
      setSelectedId((current) => {
        if (requestedJob && data.items.some((job) => job.id === requestedJob)) return requestedJob;
        if (current && data.items.some((job) => job.id === current)) return current;
        return data.items[0]?.id ?? null;
      });
    } catch (caught) {
      if (generation !== listGenerationRef.current) return;
      setError(caught instanceof Error ? caught.message : "无法加载职位。");
    } finally {
      if (generation === listGenerationRef.current) setLoading(false);
    }
  }, [listQuery, queryString, setSearchParams]);
  const loadJobsRef = useRef(loadJobs);
  useEffect(() => {
    loadJobsRef.current = loadJobs;
  }, [loadJobs]);
  const refreshJobs = useCallback(() => loadJobsRef.current(), []);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    const generation = listGenerationRef.current;
    const queryAtStart = listQuery;
    setLoadingMore(true);
    setError("");
    try {
      const params = new URLSearchParams(listQuery);
      params.set("cursor", nextCursor);
      const data = await api<{ items: JobListItem[]; next_cursor: string | null; total: number }>(
        `/api/v1/jobs?${params.toString()}`,
      );
      if (generation !== listGenerationRef.current || queryAtStart !== activeListQueryRef.current) return;
      setJobs((current) => {
        const known = new Set(current.map((job) => job.id));
        return [...current, ...data.items.filter((job) => !known.has(job.id))];
      });
      setTotal(data.total);
      setNextCursor(data.next_cursor);
    } catch (caught) {
      if (generation !== listGenerationRef.current || queryAtStart !== activeListQueryRef.current) return;
      setError(caught instanceof Error ? caught.message : "无法加载更多职位。");
    } finally {
      if (generation === listGenerationRef.current && queryAtStart === activeListQueryRef.current) {
        setLoadingMore(false);
      }
    }
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadJobs(), 0);
    return () => window.clearTimeout(timeout);
  }, [loadJobs, groups.revision]);

  useEffect(() => {
    const routeSearch = searchParams.get("q") ?? "";
    const timeout = window.setTimeout(
      () => setSearch((current) => (current === routeSearch ? current : routeSearch)),
      0,
    );
    return () => window.clearTimeout(timeout);
  }, [queryString, searchParams]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const current = searchParams.get("q") ?? "";
      if (current === search.trim()) return;
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          if (search.trim()) next.set("q", search.trim());
          else next.delete("q");
          next.delete("job");
          return next;
        },
        { replace: true },
      );
    }, 280);
    return () => clearTimeout(timeout);
  }, [search, searchParams, setSearchParams]);

  useEffect(() => {
    window.localStorage.setItem("roleworth:detail-ratio", String(detailRatio));
  }, [detailRatio]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1101px)");
    const updateMode = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    media.addEventListener("change", updateMode);
    return () => media.removeEventListener("change", updateMode);
  }, []);

  useEffect(() => {
    if (isDesktop || !searchParams.has("show_dismissed")) return;
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("show_dismissed");
        next.delete("job");
        return next;
      },
      { replace: true },
    );
  }, [isDesktop, searchParams, setSearchParams]);

  useEffect(() => {
    const activeGroup = searchParams.get("group");
    if (
      !groups.loaded ||
      !activeGroup ||
      activeGroup === "ungrouped" ||
      groups.items.some((group) => group.id === activeGroup)
    )
      return;
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("group");
        next.delete("job");
        return next;
      },
      { replace: true },
    );
  }, [groups.items, groups.loaded, searchParams, setSearchParams]);

  function selectJob(id: string, open = true) {
    setSelectedId(id);
    setDetailOpen(open);
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set("job", id);
        return next;
      },
      { replace: true },
    );
  }

  function closeDetail() {
    setDetailOpen(false);
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("job");
        return next;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    function keydown(event: KeyboardEvent) {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      )
        return;
      if (event.key.toLowerCase() === "f") {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (["j", "k"].includes(event.key.toLowerCase()) && jobs.length) {
        event.preventDefault();
        const index = Math.max(
          0,
          jobs.findIndex((job) => job.id === selectedId),
        );
        const next =
          event.key.toLowerCase() === "j" ? Math.min(jobs.length - 1, index + 1) : Math.max(0, index - 1);
        selectJob(jobs[next].id, false);
        document
          .querySelector<HTMLElement>(`[data-job-id="${jobs[next].id}"]`)
          ?.focus({ preventScroll: true });
      }
      if (event.key === "Enter" && selectedId) setDetailOpen(true);
      if (event.key === "Escape") closeDetail();
    }
    document.addEventListener("keydown", keydown);
    return () => document.removeEventListener("keydown", keydown);
  });

  async function createMobileGroup(event: FormEvent) {
    event.preventDefault();
    if (!mobileNewGroupName.trim()) return;
    try {
      const group = await groups.createGroup(mobileNewGroupName.trim());
      setMobileNewGroupName("");
      setMobileNewGroupOpen(false);
      groups.openGroup(group.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法创建分组。");
    }
  }

  function resizeFromPointer(event: ReactPointerEvent<HTMLDivElement>) {
    if (!resizing || !splitRef.current) return;
    const bounds = splitRef.current.getBoundingClientRect();
    const detailPixels = Math.min(Math.max(bounds.right - event.clientX, 500), bounds.width - 360);
    setDetailRatio(Math.min(0.75, Math.max(0.45, detailPixels / bounds.width)));
  }

  function finishResize(event: ReactPointerEvent<HTMLDivElement>) {
    if (!resizing) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setResizing(false);
  }

  function adjustDetailRatio(delta: number) {
    setDetailRatio((current) => Math.min(0.75, Math.max(0.45, current + delta)));
  }

  const activeGroup = searchParams.get("group");
  const activeGroupRecord = groups.items.find((group) => group.id === activeGroup);
  const title =
    activeGroup === "ungrouped"
      ? "未分组"
      : (activeGroupRecord?.name ?? (activeGroup ? "正在读取分组" : "全部职位"));
  const emptyText = search
    ? "没有匹配当前搜索的职位。"
    : activeGroup === "ungrouped"
      ? "所有可见职位都已经完成分组。"
      : activeGroupRecord
        ? "这个分组还没有职位，可以从列表拖入。"
        : "Agent 写入职位后会显示在这里。";

  return (
    <main className="jobs-workspace" id="main-content">
      <header className="workspace-header">
        <div className="mobile-brand">
          <span className="brand-tile">R</span>
          <label className="mobile-group-picker">
            <span className="sr-only">选择分组</span>
            <select
              value={activeGroup ?? "all"}
              onChange={(event) => groups.openGroup(event.target.value === "all" ? null : event.target.value)}
            >
              <option value="all">ALL</option>
              <option value="ungrouped">未分组</option>
              {groups.items.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            <ChevronDown aria-hidden="true" />
          </label>
          <button
            className="mobile-new-group"
            type="button"
            aria-label="创建分组"
            onClick={() => setMobileNewGroupOpen((value) => !value)}
          >
            <Plus aria-hidden="true" />
          </button>
          <Link className="icon-button mobile-settings-link" to="/settings" aria-label="设置与 Agent API">
            <Settings aria-hidden="true" />
          </Link>
        </div>
        {mobileNewGroupOpen && (
          <form className="mobile-new-group-form" onSubmit={createMobileGroup}>
            <input
              autoFocus
              required
              maxLength={60}
              placeholder="分组名称"
              value={mobileNewGroupName}
              onChange={(event) => setMobileNewGroupName(event.target.value)}
            />
            <button className="button secondary" type="submit">
              创建
            </button>
          </form>
        )}
        <div className="title-row">
          <div>
            <p className="eyebrow">机会资料库</p>
            <h1>
              {title} <span>{total}</span>
            </h1>
          </div>
          <Link className="icon-button desktop-settings-link" to="/settings" aria-label="设置与 Agent API">
            <Settings aria-hidden="true" />
          </Link>
        </div>
        <div className="toolbar-row">
          <label className="search-box">
            <Search aria-hidden="true" />
            <span className="sr-only">搜索职位</span>
            <input
              ref={searchRef}
              type="search"
              placeholder="搜索公司、职位、技能或 JD"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <kbd>F</kbd>
          </label>
          <select
            className="filter-chip availability-filter"
            aria-label="筛选招聘状态"
            value={searchParams.get("availability") ?? ""}
            onChange={(event) =>
              setSearchParams((params) => {
                const next = new URLSearchParams(params);
                if (event.target.value) next.set("availability", event.target.value);
                else next.delete("availability");
                next.delete("job");
                return next;
              })
            }
          >
            <option value="">全部招聘状态</option>
            <option value="active">可跟进</option>
            {Object.entries(availabilityLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <label
            className={`filter-chip dismissed-toggle desktop-only ${searchParams.has("show_dismissed") ? "is-active" : ""}`}
          >
            <input
              type="checkbox"
              checked={searchParams.has("show_dismissed")}
              onChange={(event) =>
                setSearchParams((params) => {
                  const next = new URLSearchParams(params);
                  if (event.target.checked) next.set("show_dismissed", "true");
                  else next.delete("show_dismissed");
                  next.delete("job");
                  return next;
                })
              }
            />
            <span>显示暂不考虑</span>
          </label>
        </div>
      </header>

      <div
        className={`split-layout ${resizing ? "is-resizing" : ""}`}
        ref={splitRef}
        style={{ "--detail-ratio": `${detailRatio * 100}%` } as CSSProperties}
      >
        <section className="job-list" aria-label="职位列表">
          <div className="table-head" aria-hidden="true">
            <span>职位</span>
            <span>地点</span>
            <span>薪资</span>
            <span>AI 评分 ↓</span>
          </div>
          {loading ? (
            <div className="list-state">正在读取职位</div>
          ) : error ? (
            <div className="list-state error-state">
              <p>{error}</p>
              <button className="button secondary" type="button" onClick={() => void loadJobs()}>
                重试
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="empty-jobs">
              <BriefcaseBusiness />
              <h2>还没有符合条件的职位</h2>
              <p>{emptyText}</p>
            </div>
          ) : (
            <div className="job-rows">
              {jobs.map((job, index) => {
                const compensation = formatCompensation(job);
                return (
                  <button
                    key={job.id}
                    data-job-id={job.id}
                    className={`job-row ${selectedId === job.id ? "is-selected" : ""}`}
                    type="button"
                    draggable
                    aria-pressed={selectedId === job.id}
                    onClick={() => selectJob(job.id)}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("application/x-roleworth-job", job.id);
                      groups.setDraggedJob({
                        id: job.id,
                        title: job.title,
                        company: job.company,
                        groupId: job.group?.id ?? null,
                      });
                    }}
                    onDragEnd={() => groups.setDraggedJob(null)}
                  >
                    <span
                      className="job-drag-handle"
                      aria-hidden="true"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <GripVertical />
                    </span>
                    <span className="job-primary">
                      <span className={`company-avatar tone-${index % 5}`}>
                        {job.company.slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        <strong>{job.title}</strong>
                        <small>
                          {job.company}
                          {job.team ? ` · ${job.team}` : ""}
                          <span className={`availability-badge availability-${job.availability.status}`}>
                            {availabilityLabels[job.availability.status]}
                          </span>
                        </small>
                      </span>
                    </span>
                    <span className="job-location">
                      {job.location_label ?? "地点待确认"}
                      <small>{workplaceLabels[job.workplace_type]}</small>
                    </span>
                    <span className="job-salary">
                      {compensation.main}
                      <small>{compensation.period}</small>
                    </span>
                    {job.latest_agent_score == null ? (
                      <span className="score score-empty">—</span>
                    ) : (
                      <span className={`score ${job.latest_agent_score >= 83 ? "score-high" : "score-mid"}`}>
                        {job.latest_agent_score}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {!!jobs.length && (
            <div className="list-footer">
              <span>
                显示 {jobs.length} / {total} 个职位
              </span>
              {nextCursor ? (
                <button className="button secondary" type="button" disabled={loadingMore} onClick={loadMore}>
                  {loadingMore ? "正在加载" : "加载更多"}
                </button>
              ) : (
                <span className="keyboard-hint">
                  <kbd>J</kbd>
                  <kbd>K</kbd> 选择 · <kbd>Enter</kbd> 阅读
                </span>
              )}
            </div>
          )}
        </section>
        <div
          className="pane-resizer"
          role="separator"
          aria-label="调整职位详情宽度"
          aria-orientation="vertical"
          aria-valuemin={45}
          aria-valuemax={75}
          aria-valuenow={Math.round(detailRatio * 100)}
          tabIndex={0}
          onDoubleClick={() => adjustDetailRatio(0.6 - detailRatio)}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            setResizing(true);
          }}
          onPointerMove={resizeFromPointer}
          onPointerUp={finishResize}
          onPointerCancel={finishResize}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              adjustDetailRatio(0.02);
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              adjustDetailRatio(-0.02);
            }
          }}
        />
        <JobDetailPane
          key={selectedId ?? "empty"}
          jobId={selectedId}
          open={detailOpen}
          onClose={closeDetail}
          onChanged={refreshJobs}
        />
      </div>
    </main>
  );
}

function JobDetailPane({
  jobId,
  open,
  onClose,
  onChanged,
}: {
  jobId: string | null;
  open: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const groups = useGroups();
  type Comment = { id: string; actor_type: string; actor_name: string; body: string; created_at: string };
  type Snapshot = { id: string; description: string; fetched_at: string; created_at: string };

  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [tab, setTab] = useState<"description" | "assessment" | "comments" | "history">("description");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [assessmentCursor, setAssessmentCursor] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentCursor, setCommentCursor] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [snapshotCursor, setSnapshotCursor] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [comment, setComment] = useState("");
  const [tagName, setTagName] = useState("");

  const loadDetail = useCallback(async () => {
    if (!jobId) return setDetail(null);
    setLoading(true);
    setError("");
    try {
      setDetail(await api<JobDetail>(`/api/v1/jobs/${jobId}`));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "无法读取职位详情。");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setTab("description");
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [jobId]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void loadDetail(), 0);
    return () => window.clearTimeout(timeout);
  }, [groups.revision, loadDetail]);

  useEffect(() => {
    if (!jobId) return;
    if (tab === "assessment")
      void api<{ items: Assessment[]; next_cursor: string | null }>(`/api/v1/jobs/${jobId}/assessments`).then(
        (data) => {
          setAssessments(data.items);
          setAssessmentCursor(data.next_cursor);
        },
      );
    if (tab === "comments")
      void api<{ items: Comment[]; next_cursor: string | null }>(`/api/v1/jobs/${jobId}/comments`).then(
        (data) => {
          setComments(data.items);
          setCommentCursor(data.next_cursor);
        },
      );
    if (tab === "history")
      void api<{ items: Snapshot[]; next_cursor: string | null }>(`/api/v1/jobs/${jobId}/snapshots`).then(
        (data) => {
          setSnapshots(data.items);
          setSnapshotCursor(data.next_cursor);
        },
      );
  }, [jobId, tab]);

  async function loadMoreHistory(kind: "assessment" | "comments" | "history") {
    if (!jobId || loadingHistory) return;
    const cursor =
      kind === "assessment" ? assessmentCursor : kind === "comments" ? commentCursor : snapshotCursor;
    if (!cursor) return;
    setLoadingHistory(true);
    try {
      if (kind === "assessment") {
        const data = await api<{ items: Assessment[]; next_cursor: string | null }>(
          `/api/v1/jobs/${jobId}/assessments?cursor=${encodeURIComponent(cursor)}`,
        );
        setAssessments((current) => [...current, ...data.items]);
        setAssessmentCursor(data.next_cursor);
      } else if (kind === "comments") {
        const data = await api<{ items: Comment[]; next_cursor: string | null }>(
          `/api/v1/jobs/${jobId}/comments?cursor=${encodeURIComponent(cursor)}`,
        );
        setComments((current) => [...current, ...data.items]);
        setCommentCursor(data.next_cursor);
      } else {
        const data = await api<{ items: Snapshot[]; next_cursor: string | null }>(
          `/api/v1/jobs/${jobId}/snapshots?cursor=${encodeURIComponent(cursor)}`,
        );
        setSnapshots((current) => [...current, ...data.items]);
        setSnapshotCursor(data.next_cursor);
      }
    } finally {
      setLoadingHistory(false);
    }
  }

  async function updateReview(
    status: ReviewStatus,
    rating = detail?.review.rating ?? null,
    note = detail?.review.note ?? "",
  ) {
    if (!jobId || !detail) return;
    await api(`/api/v1/jobs/${jobId}/review`, {
      method: "PUT",
      body: JSON.stringify({ status, rating, note }),
    });
    await loadDetail();
    await onChanged();
    await groups.refreshGroups();
  }

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (!jobId || !comment.trim()) return;
    await api(`/api/v1/jobs/${jobId}/comments`, { method: "POST", body: JSON.stringify({ body: comment }) });
    setComment("");
    const data = await api<{ items: Comment[]; next_cursor: string | null }>(
      `/api/v1/jobs/${jobId}/comments`,
    );
    setComments(data.items);
    setCommentCursor(data.next_cursor);
    await loadDetail();
  }

  async function addTag(event: FormEvent) {
    event.preventDefault();
    if (!jobId || !tagName.trim()) return;
    await api(`/api/v1/jobs/${jobId}/tags`, { method: "POST", body: JSON.stringify({ name: tagName }) });
    setTagName("");
    await loadDetail();
    await onChanged();
  }

  async function changeGroup(groupId: string | null) {
    if (!jobId) return;
    try {
      await groups.moveJob(jobId, groupId);
      await loadDetail();
    } catch {
      // The group context already reports the failure in the workspace notice.
    }
  }

  if (!jobId)
    return (
      <aside className={`detail-pane ${open ? "is-open" : ""}`}>
        <div className="detail-placeholder">
          <BriefcaseBusiness />
          <p>选择一个职位查看完整 JD 和评审。</p>
        </div>
      </aside>
    );

  return (
    <article className={`detail-pane ${open ? "is-open" : ""}`} aria-label="职位详情">
      <div className="detail-toolbar">
        <button className="mobile-back" type="button" onClick={onClose}>
          <ArrowLeft />
          返回列表
        </button>
        <span className="detail-timestamp">
          {detail ? `${formatRelative(detail.last_found_at)}发现` : "正在读取"}
        </span>
        <div>
          <button
            className="icon-button"
            type="button"
            aria-label="复制职位链接"
            onClick={() => void navigator.clipboard.writeText(window.location.href)}
          >
            <Copy />
          </button>
          <button
            className="icon-button detail-close-icon"
            type="button"
            aria-label="关闭详情"
            onClick={onClose}
          >
            <X />
          </button>
        </div>
      </div>
      <div className="detail-scroll">
        {loading && !detail ? (
          <div className="detail-state">正在读取职位详情</div>
        ) : error ? (
          <div className="detail-state error-state">{error}</div>
        ) : (
          detail && (
            <>
              <header className="job-detail-header">
                <div className="detail-company-row">
                  <span className="company-avatar large tone-0">
                    {detail.company.slice(0, 1).toUpperCase()}
                  </span>
                  <div>
                    <span>{detail.company}</span>
                    <small>{detail.team ?? detail.source}</small>
                  </div>
                </div>
                <h2>{detail.title}</h2>
                <p className="detail-meta">
                  <span>
                    {detail.location_label ?? "地点待确认"} · {workplaceLabels[detail.workplace_type]}
                  </span>
                  <span>{detail.employment_type.replaceAll("_", " ")}</span>
                  {detail.level && <span>{detail.level}</span>}
                </p>
                <div className="detail-actions">
                  <button
                    className={`button secondary review-toggle ${detail.review.status === "dismissed" ? "is-dismissed" : ""}`}
                    type="button"
                    onClick={() =>
                      void updateReview(detail.review.status === "dismissed" ? "inbox" : "dismissed")
                    }
                  >
                    {detail.review.status === "dismissed" ? <RotateCcw /> : <EyeOff />}
                    {detail.review.status === "dismissed" ? "重新考虑" : "暂不考虑"}
                  </button>
                  <a
                    className="button secondary"
                    href={detail.apply_url ?? detail.source_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    打开来源 <ExternalLink />
                  </a>
                </div>
                <label className="detail-group-picker">
                  <Folder aria-hidden="true" />
                  <span>分组</span>
                  <select
                    aria-label="移动到分组"
                    value={detail.group?.id ?? ""}
                    onChange={(event) => void changeGroup(event.target.value || null)}
                  >
                    <option value="">未分组</option>
                    {groups.items.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown aria-hidden="true" />
                </label>
                <form className="inline-tag-form" onSubmit={addTag}>
                  <TagIcon aria-hidden="true" />
                  {detail.tags.map((tag) => (
                    <span className="detail-tag" key={tag.id}>
                      {tag.name}
                    </span>
                  ))}
                  <input
                    aria-label="添加标签"
                    placeholder="添加标签"
                    value={tagName}
                    onChange={(event) => setTagName(event.target.value)}
                  />
                </form>
              </header>
              <AvailabilityPanel
                key={`${detail.id}-${detail.availability.checked_at ?? "unchecked"}`}
                job={detail}
                onChanged={async () => {
                  await loadDetail();
                  await onChanged();
                }}
              />
              <section className="decision-strip" aria-label="评审摘要">
                <div>
                  <span>Agent 匹配</span>
                  <strong className="score-text">{detail.latest_agent_score ?? "—"}</strong>
                  <small>
                    {detail.latest_assessment
                      ? `置信度${detail.latest_assessment.confidence === "high" ? "高" : detail.latest_assessment.confidence === "medium" ? "中" : "低"}`
                      : "尚无评估"}
                  </small>
                </div>
                <div>
                  <span>个人评分</span>
                  <strong className="rating-stars" aria-label={`${detail.review.rating ?? 0} 星`}>
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        type="button"
                        aria-label={`评为 ${rating} 星`}
                        className={rating <= (detail.review.rating ?? 0) ? "is-filled" : ""}
                        onClick={() => void updateReview(editableReviewStatus(detail.review.status), rating)}
                      >
                        ★
                      </button>
                    ))}
                  </strong>
                  <small>{detail.review.rating ? "你的最终判断" : "点击星级评分"}</small>
                </div>
                <div>
                  <span>个人处理</span>
                  <strong>{detail.review.status === "dismissed" ? "暂不考虑" : "收件箱"}</strong>
                  <small>
                    {detail.review.note ||
                      (detail.review.status === "dismissed" ? "默认不在列表显示" : "保留在职位列表")}
                  </small>
                </div>
              </section>
              <nav className="detail-tabs" aria-label="职位详情分区">
                <button
                  className={tab === "description" ? "is-active" : ""}
                  type="button"
                  onClick={() => setTab("description")}
                >
                  职位描述
                </button>
                <button
                  className={tab === "assessment" ? "is-active" : ""}
                  type="button"
                  onClick={() => setTab("assessment")}
                >
                  Agent 评估 <span>{detail.counts.assessments}</span>
                </button>
                <button
                  className={tab === "comments" ? "is-active" : ""}
                  type="button"
                  onClick={() => setTab("comments")}
                >
                  评论 <span>{detail.counts.comments}</span>
                </button>
                <button
                  className={tab === "history" ? "is-active" : ""}
                  type="button"
                  onClick={() => setTab("history")}
                >
                  历史 <span>{detail.counts.snapshots}</span>
                </button>
              </nav>
              {tab === "description" && (
                <div className="reading-column">
                  <div className="snapshot-note">
                    抓取于 {new Date(detail.current_snapshot.fetched_at).toLocaleString("zh-CN")} · 当前 JD
                    快照
                  </div>
                  <div className="jd-copy" lang={detail.language}>
                    {detail.current_snapshot.description}
                  </div>
                  {detail.skills.length > 0 && (
                    <div className="skill-list" aria-label="技能">
                      {detail.skills.map((skill) => (
                        <span key={skill}>{skill}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tab === "assessment" && (
                <div className="reading-column tab-column">
                  {assessments.length ? (
                    assessments.map((assessment) => (
                      <section className="assessment-entry" key={assessment.id}>
                        <header>
                          <div>
                            <span>Agent 评估</span>
                            <small>
                              {assessment.actor_name} ·{" "}
                              {new Date(assessment.generated_at).toLocaleString("zh-CN")}
                            </small>
                          </div>
                          <strong>{assessment.overall_score}</strong>
                        </header>
                        <p>{assessment.summary}</p>
                        <div className="dimension-grid">
                          {Object.entries(assessment.dimensions).map(([name, value]) => (
                            <span key={name}>
                              <small>{name.replaceAll("_", " ")}</small>
                              <strong>{value}</strong>
                            </span>
                          ))}
                        </div>
                        {assessment.reasons.length > 0 && (
                          <ul>
                            {assessment.reasons.map((reason) => (
                              <li key={reason}>{reason}</li>
                            ))}
                          </ul>
                        )}
                      </section>
                    ))
                  ) : (
                    <TabEmpty icon={<BriefcaseBusiness />} text="这个职位还没有 Agent 评估。" />
                  )}
                  {assessmentCursor && (
                    <button
                      className="button secondary history-pagination"
                      type="button"
                      disabled={loadingHistory}
                      onClick={() => void loadMoreHistory("assessment")}
                    >
                      {loadingHistory ? "正在加载" : "加载更早评估"}
                    </button>
                  )}
                </div>
              )}
              {tab === "comments" && (
                <div className="reading-column tab-column">
                  <form className="comment-form" onSubmit={submitComment}>
                    <MessageSquareText />
                    <textarea
                      rows={3}
                      placeholder="记录需要确认的问题或下一步"
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                    />
                    <button className="button primary" type="submit" disabled={!comment.trim()}>
                      添加评论
                    </button>
                  </form>
                  {comments.length ? (
                    comments.map((item) => (
                      <article className="comment-entry" key={item.id}>
                        <header>
                          <strong>{item.actor_name}</strong>
                          <span>
                            {item.actor_type === "agent" ? "Agent" : "用户"} ·{" "}
                            {formatRelative(item.created_at)}
                          </span>
                        </header>
                        <p>{item.body}</p>
                      </article>
                    ))
                  ) : (
                    <TabEmpty icon={<MessageSquareText />} text="还没有评论，写下第一个待确认事项。" />
                  )}
                  {commentCursor && (
                    <button
                      className="button secondary history-pagination"
                      type="button"
                      disabled={loadingHistory}
                      onClick={() => void loadMoreHistory("comments")}
                    >
                      {loadingHistory ? "正在加载" : "加载更早评论"}
                    </button>
                  )}
                </div>
              )}
              {tab === "history" && (
                <div className="reading-column tab-column">
                  {snapshots.map((snapshot, index) => (
                    <details className="snapshot-entry" key={snapshot.id} open={index === 0}>
                      <summary>
                        <span>JD 第 {detail.counts.snapshots - index} 版</span>
                        <small>{new Date(snapshot.fetched_at).toLocaleString("zh-CN")}</small>
                      </summary>
                      <div className="jd-copy compact-copy">{snapshot.description}</div>
                    </details>
                  ))}
                  {snapshotCursor && (
                    <button
                      className="button secondary history-pagination"
                      type="button"
                      disabled={loadingHistory}
                      onClick={() => void loadMoreHistory("history")}
                    >
                      {loadingHistory ? "正在加载" : "加载更早版本"}
                    </button>
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </article>
  );
}

function TabEmpty({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="tab-empty">
      {icon}
      <p>{text}</p>
    </div>
  );
}
